import axios, { clientRecords, createBookmark, deleteBookmark, projectApi, projectManagementApi } from '@atheneum/api'
import { isCancellationError, showDialog } from '@components/Dialog'
import { formatStartToEndDate } from '@atheneum/api/dist/lib'
import Project from '@models/Project.model'
import get from 'lodash/get'
import { computed, extendObservable } from 'mobx'
import ClientFeedbackStore from './clientFeedback.store'
import ExpertFeedbackStore from './expertFeedback.store'
import MetricStore from './metric.store'

class ProjectStore {
  constructor(
    staticData,
    segmentStore,
    growlStore,
    modalStore,
    userStore,
    activeProjectStore
  ) {
    extendObservable(this, {
      id: null,
      segmentStore,
      staticData,
      growlStore,
      modalStore,
      userStore,
      activeProjectStore,
      project: null,
      deliverables: [],
      attachments: [],
      loading: false,
      loadingContacts: false,
      error: null,
      actions: [],
      capiDetails: {},
      loggedEvents: { project: null, epls: {} },
      projectStatus: computed(this.getProjectStatus),
      projectInterviewedExperts: null,
      projectInterviewedExpertsLoading: false,
      projectStartEndDate: computed(this.renderStartEndDate),
      metricStore: new MetricStore(),
      expertFeedbackStore: new ExpertFeedbackStore(),
      clientFeedbackStore: new ClientFeedbackStore(),
      industry: computed(() => {
        if (!this.project) return ''
        return staticData.names.industries[this.project.industryId]
      }),
      subIndustry: computed(() => {
        if (!this.project) return ''
        return staticData.names.subIndustries[this.project.subIndustryId]
      }),
      projectBackground: computed(() => {
        if (!this.project) return ''
        if (!this.project.background) {
          return '-'
        } else {
          return this.project.background
        }
      }),
      caseCodeRequired: false,
      projects: [],
      similarProjects: [],
      loadingSimilar: false,
      applicableContracts: []
    })
    projectManagementApi.getActions().then(actions => this.actions.replace(actions))
  }

  updateAction = async actionId => {
    await projectManagementApi.updateAction({ actionId, projectId: this.project.id })
    this.project.actionId = actionId
    this.growlStore.showSuccess('Updated project action')
  }

  updatePriority = async priorityId => {
    await projectManagementApi.updateAction({ priorityId, projectId: this.project.id })
    this.project.priorityId = priorityId
    this.growlStore.showSuccess('Updated project priority')
  }

  getAttachments(id) {
    return projectApi
      .getProjectAttachments(id)
      .then(attachments => {
        this.attachments.replace(attachments)
      })
      .catch(err => {
        this.onError(err)
      })
  }

  async get(id) {
    this.id = Number(id)
    this.total = null
    this.loading = true
    this.project = null
    await this.segmentStore.getAll(id)
    return projectApi
      .getOne(id)
      .then(project => {
        this.project = new Project(project, this.staticData)

        // @TODO extract this to a separate store
        this.getProjectInterviewedExperts(id)
        this.getAttachments(id)
        this.getCaseCodeRequired(this.project.parentAccountId)
        this.loadApplicableContracts(id)
        this.loading = false
        if (!this.activeProjectStore.isActiveProjectLocked) {
          this.activeProjectStore.markProjectAsActive(id, project.projectName)
        }

        this.check(id).then(res => {
          const capiDetails = get(res, 'data.project', undefined)

          if(capiDetails?.capi_client_id !== 'internal') {
            this.capiDetails = capiDetails
          } else {
            this.capiDetails = undefined
          }
        })
      })
      .catch(err => {
        this.error = err
        this.loading = false
      })
  }

  getCaseCodeRequired = parentAccountId => {
    return clientRecords.getParentAccount(parentAccountId)
      .then(parentAccount => {
        this.caseCodeRequired = parentAccount.caseCodeRequired
      })
      .catch(err => {
        this.onError(err)
      })
  }

  check = id => {
    this.loading = true
    return axios({
      method: 'get',
      url: `${process.env.REACT_APP_CAPI_SERVICE_URL}/check?p=${id}`,
    })
      .then(res => {
        this.loading = false
        return res
      })
      .catch(e => {
        this.loading = false
        console.error(e)
        return null
      })
  }

  deleteAttachment = id => {
    this.loading = true
    return projectApi
      .deleteProjectAttachments(id)
      .then(() => {
        this.loading = false
        this.error = null

        return this.getAttachments(this.project.id)
      })
      .catch(err => {
        this.loading = false
        this.error = err
      })
  }

  delete = ({ deletePulse }) => {
    return projectApi
      .remove(this.project.id, deletePulse)
      .then(res => {
        this.growlStore.showSuccess('Project successfully deleted.')
        if (res.archivedPulse === true) {
          this.growlStore.showSuccess(
            `Monday pulse for project has been removed.`
          )
        } else if (res.archivedPulse === false) {
          this.growlStore.showError(
            `Couldn't archive Monday pulse. Please remove it manually.`
          )
        }
        return res
      })
      .catch(err => {
        this.error = err
        this.growlStore.showError(this.error.message)

        throw err
      })
  }

  regenerateCode(id) {
    return projectApi
      .regenerateProjectCode(id)
      .then(res => {
        showDialog({
          title: 'Code regenerated!',
          type: 'success'
        }).catch(err => isCancellationError(err))
        this.get(this.id)
        this.error = null
      })
      .catch(err => {
        this.error = err
        return showDialog({
          title: 'There was an error!',
          type: 'error'
        }).catch(err => isCancellationError(err))
      })
  }

  bookmark(id) {
    const payload = { projectId: id }
    return createBookmark(payload)
      .then(() => {
        this.get(this.id)
        this.error = null
      })
      .catch(err => {
        this.error = err
      })
  }

  deleteBookmark(id) {
    return deleteBookmark(id)
      .then(() => {
        this.get(this.id)
        this.error = null
      })
      .catch(err => {
        this.error = err
      })
  }

  getProjectStatus() {
    return this.staticData.getDataFromArray(
      this.project.projectStatusId,
      'projectStatuses'
    )
  }

  getProjectInterviewedExperts(id) {
    this.projectInterviewedExpertsLoading = true
    return projectApi
      .getInterviewedExperts(id)
      .then(interviewedExperts => {
        this.projectInterviewedExperts = interviewedExperts
        this.projectInterviewedExpertsLoading = false
      })
      .catch(err => {
        this.error = err
        this.projectInterviewedExpertsLoading = false
      })
  }

  renderStartEndDate() {
    return formatStartToEndDate({
      start: this.project.startDate,
      end: this.project.endDate
    })
  }

  saveInterviewTarget() {
    return projectApi.saveInterviewTarget(this.project.id, {
      interviewTarget: this.project.interviewTarget
    })
  }

  setProjectCategory(categoryId) {
    this.project.projectCategoryId = categoryId
  }

  changeStatus = status => {
    if (status[0] === this.staticData.CONST.PROJECT_STATUSES.CLOSED) {
      this.modalStore.show({
        type: 'CLOSING_NOTE',
        props: {
          projectId: this.project.id,
          caseCodeRequired: this.caseCodeRequired,
          projectStore: this
        }
      })
    } else {
      return this.sendStatusToServer({ projectStatusId: status[0] })
    }
  }

  sendAdHocFeedback = () => {
    return projectApi
      .sendAdHocFeedback(this.id)
      .then(() => {
        this.clientFeedbackStore.load(this.id)
        this.growlStore.showSuccess('Feedback sent')
      })
      .catch(err => {
        this.error = err
        this.growlStore.showError(err)

        throw err
      })
  }

  sendStatusToServer = data => {
    return projectApi
      .updateProjectStatus(this.id, data)
      .then(() => {
        this.project.projectStatusId = data.projectStatusId
        this.growlStore.showSuccess('Project status updated')
      })
      .catch(err => {
        this.error = err
        this.growlStore.showError(err)

        throw err
      })
  }

  getPageCountForProject(projectId) {
    return projectApi
      .getLoggedEvents({ eventType: 'project_open', eventIds: [projectId] })
      .then(res => {
        const events = res.map(event => {
          return {
            id: event.eventId,
            count: event.count,
            type: event.eventType
          }
        })

        if (events.length) {
          this.loggedEvents.project = events[0]
        }
      })
      .catch(err => {
        this.error = err
      })
  }

  getPageCountForEpls(eplIds) {
    return projectApi
      .getLoggedEvents({ eventType: 'epl_open', eventIds: eplIds })
      .then(res => {
        const events = res.reduce((acc, event) => {
          acc[event.eventId] = event.count

          return acc
        }, {})

        this.loggedEvents.epls = events
      })
      .catch(err => {
        this.error = err
      })
  }

  loadSimilarProjects() {
    this.loadingSimilar = true
    return projectApi
      .getSimilarProjects(this.id)
      .then(res => {
        const projects = res.map(project => {
          return { ...project, value: true }
        })
        const similarProjects = res.map(project => {
          return {
            value: project.id,
            label: project.name
          }
        })
        this.projects.replace(projects)
        this.similarProjects.replace(similarProjects)
        this.loadingSimilar = false
      })
      .catch(err => {
        this.error = err
      })
  }

  isCreditBasedContract = () => {
    const { PAYG } = this.staticData.CONST.CONTRACT_TYPES
    // If there is no contract we will treat it as PAYG since we have no credit value to work with
    const contract = this.applicableContracts && this.applicableContracts.length > 0 ? this.applicableContracts[0] : null

    if (!contract) return false

    // If flag is set treat it as Non credit based
    if (contract.marginPredictionInFeeValue) return false

    // PACKAGE and SUBSCRIPTION are credit based PAYG is not
    return contract && contract.contractTypeId !== PAYG
  }

  async loadApplicableContracts(projectId) {
    const applicableContracts = await projectApi.getApplicableContracts(
      projectId
    )
    this.applicableContracts.replace(applicableContracts)
  }
}

export default ProjectStore
